import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Grid,
  TextField,
  Typography,
  Paper,
  Button,
  IconButton,
  Stack,
  useTheme,
  useMediaQuery,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Container
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CancelIcon from '@mui/icons-material/Cancel';
import AddIcon from '@mui/icons-material/Add';
import LocationSelector from './LocationSelector';

// Import the image
import fixtureImage from '../../../assets/images/profile-1.png';

// Dummy data for fixtures
const dummyFixtures = [
  {
    id: 1,
    name: 'Fixture 1',
    image: fixtureImage,
    storeId: 'ST001',
    Brands: ['Electronics', 'Home Appliances'],
    status: 'active'
  },
  {
    id: 2,
    name: 'Fixture 2',
    image: fixtureImage,
    storeId: 'ST002',
    Brands: ['Fashion', 'Accessories'],
    status: 'inactive'
  },
  {
    id: 3,
    name: 'Fixture 3',
    image: fixtureImage,
    storeId: 'ST003',
    Brands: ['Sports', 'Fitness'],
    status: 'active'
  },
  {
    id: 4,
    name: 'Fixture 4',
    image: fixtureImage,
    storeId: 'ST004',
    Brands: ['Books', 'Stationery'],
    status: 'inactive'
  },
  {
    id: 5,
    name: 'Fixture 5',
    image: fixtureImage,
    storeId: 'ST005',
    Brands: ['Toys', 'Games'],
    status: 'active'
  }
];



const VMFixtures = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [selectedFixture, setSelectedFixture] = useState(null);
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    name: '',
    referenceImage: null,
    description: ''
  });
  const [locationType, setLocationType] = useState('panIndia');
  const [selectedZone, setSelectedZone] = useState('');
  const [selectedState, setSelectedState] = useState('');
  const [selectedCity, setSelectedCity] = useState('');
  const [selectedStores, setSelectedStores] = useState([]);
  const [submittedFixtures, setSubmittedFixtures] = useState({});
  const [fixtures, setFixtures] = useState(dummyFixtures);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [newFixtureData, setNewFixtureData] = useState({
    name: '',
    image: null,
    imagePreview: fixtureImage,
    Brands: '',
    storeId: ''
  });

  const handleAssignVMClick = (fixture) => {
    setSelectedFixture(fixture);
    if (submittedFixtures[fixture.id]) {
      const savedData = submittedFixtures[fixture.id];
      setFormData({
        name: savedData.formData.name || fixture.name,
        referenceImage: savedData.formData.referenceImage || null,
        description: savedData.formData.description || ''
      });
      setLocationType(savedData.locationType);
      setSelectedZone(savedData.selectedZone);
      setSelectedState(savedData.selectedState);
      setSelectedCity(savedData.selectedCity);
      setSelectedStores(savedData.selectedStores);
      setStep(1);
    } else {
      setFormData({ name: fixture.name, referenceImage: null, description: '' });
      resetLocationSelections();
      setStep(1);
    }
  };

  const resetLocationSelections = () => {
    setSelectedZone('');
    setSelectedState('');
    setSelectedCity('');
    setSelectedStores([]);
  };

  const handleFormChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleAssignVMFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData({ ...formData, referenceImage: file });
    }
  };

  // const handleLocationTypeChange = (e) => {
  //   const newLocationType = e.target.value;
  //   setLocationType(newLocationType);
  //   if (newLocationType === 'panIndia') {
  //     resetLocationSelections();
  //   }
  // };

  const handleLocationSelectionChange = (selections) => {
    const { zones, states, cities, stores } = selections;
    setSelectedZone(zones[0] || '');
    setSelectedState(states[0] || '');
    setSelectedCity(cities[0] || '');
    setSelectedStores(stores || []);
  };

  const handleSaveAndNext = () => {
    setStep(2);
  };

  const handleSubmit = () => {
    console.log('Assign VM form submitted:', formData, locationType, selectedZone, selectedState, selectedCity, selectedStores);
    const fixtureData = {
      formData,
      locationType,
      selectedZone,
      selectedState,
      selectedCity,
      selectedStores
    };
    setSubmittedFixtures((prev) => ({
      ...prev,
      [selectedFixture.id]: fixtureData
    }));
    setSelectedFixture(null);
  };

  const handleBack = () => {
    navigate('/main/user-profile');
  };

  const handleCreateModalOpen = () => {
    setIsCreateModalOpen(true);
  };

  const handleCreateModalClose = () => {
    setIsCreateModalOpen(false);
    setNewFixtureData({
      name: '',
      image: null,
      imagePreview: fixtureImage,
      Brands: '',
      storeId: ''
    });
  };

  const handleNewFixtureChange = (e) => {
    setNewFixtureData({ ...newFixtureData, [e.target.name]: e.target.value });
  };

  const handleNewFixtureFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setNewFixtureData({ ...newFixtureData, image: file, imagePreview: reader.result });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCreateFixtureSubmit = () => {
    const newId = fixtures.length > 0 ? Math.max(...fixtures.map((f) => f.id)) + 1 : 1;
    const newStoreId = `ST${String(newId).padStart(3, '0')}`;
    const newEntry = {
      id: newId,
      name: newFixtureData.name,
      image: newFixtureData.imagePreview,
      storeId: newStoreId,
      Brands: newFixtureData.Brands.split(',')
        .map((d) => d.trim())
        .filter((d) => d),
      status: 'active'
    };

    setFixtures([...fixtures, newEntry]);

    console.log('Creating new fixture:', newEntry);
    handleCreateModalClose();
  };

  const renderLocationSelection = () => (
    <LocationSelector
      onSelectionChange={handleLocationSelectionChange}
      initialSelection={{
        zones: selectedZone ? [selectedZone] : [],
        states: selectedState ? [selectedState] : [],
        cities: selectedCity ? [selectedCity] : [],
        stores: selectedStores
      }}
    />
  );

  return (
    <Container maxWidth="xl" disableGutters>
      <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
            <Stack
            spacing={{ xs: 2, sm: 3 }}
            mb={{ xs: 2, sm: 3 }}
            >
            {/* Back Button Row */}
            <Box
            sx={{
            display: 'flex',
            justifyContent: 'flex-start'
            }}
            >
            <Button
            startIcon={<ArrowBackIcon />}
            onClick={handleBack}
            variant="outlined"
            size={isMobile ? 'small' : 'medium'}
            sx={{
            borderColor: 'primary.main',
            color: 'primary.main',
            '&:hover': {
            borderColor: 'primary.dark',
            bgcolor: 'primary.light',
            color: 'primary.dark'
            }
            }}
            >
            Back
            </Button>
            </Box>

            {/* Title and Create Button Row */}
            <Stack
            direction={{ xs: 'row', sm: 'row' }}
            alignItems="center"
            justifyContent="space-between"
            spacing={2}
            sx={{ flexWrap: 'wrap' }}
            >
            <Typography
            variant="h4"
            sx={{
            color: '#0A2647',
            fontWeight: 600,
            fontSize: { xs: '1.5rem', sm: '1.8rem', md: '2.0rem' },
            height: { xs: '32px', sm: '40px' },
            lineHeight: { xs: '32px', sm: '40px' },
            textAlign: 'left',
            pl: { xs: 1, sm: 2 }
            }}
            >
            Fixtures
            </Typography>

            <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateModalOpen}
            size={isMobile ? 'small' : 'medium'}
            sx={{
            bgcolor: 'primary.main',
            '&:hover': {
            bgcolor: 'primary.dark'
            }
            }}
            >
            Create Fixture
            </Button>
            </Stack>
            </Stack>


      <Grid container spacing={{ xs: 2, sm: 3 }}>
        {fixtures.map((fixture) => (
          <Grid item xs={12} sm={6} md={4} key={fixture.id}>
            <Paper
              elevation={3}
              sx={{
                p: { xs: 1.5, sm: 2 },
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                bgcolor: 'background.paper',
                borderRadius: 2,
                boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  transition: 'transform 0.2s ease-in-out'
                }
              }}
            >
              <Box sx={{ 
                display: 'flex', 
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: { xs: 'center', sm: 'flex-start' },
                gap: { xs: 1.5, sm: 2 }, 
                mb: 2 
              }}>
                <Box
                  component="img"
                  src={fixture.image}
                  alt={fixture.name}
                  sx={{
                    width: { xs: 80, sm: 100 },
                    height: { xs: 80, sm: 100 },
                    objectFit: 'cover',
                    borderRadius: 1
                  }}
                />
                <Box sx={{ 
                  flex: 1,
                  width: { xs: '100%', sm: 'auto' },
                  textAlign: { xs: 'center', sm: 'left' }
                }}>
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      color: '#0A2647', 
                      fontWeight: 600, 
                      mb: 1,
                      fontSize: { xs: '1rem', sm: '1.25rem' }
                    }}
                  >
                    {fixture.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Store ID: {fixture.storeId}
                  </Typography>
                  <Box sx={{ 
                    display: 'flex', 
                    flexWrap: 'wrap', 
                    gap: 0.5,
                    justifyContent: { xs: 'center', sm: 'flex-start' }
                  }}>
                    {fixture.Brands.map((dept, index) => (
                      <Chip
                        key={index}
                        label={dept}
                        size="small"
                        sx={{
                          bgcolor: 'primary.light',
                          color: 'black',
                          '& .MuiChip-label': { px: 1 }
                        }}
                      />
                    ))}
                  </Box>
                </Box>
              </Box>
              <Box sx={{ 
                mt: 'auto', 
                pt: { xs: 1, sm: 2 }, 
                display: 'flex', 
                justifyContent: { xs: 'center', sm: 'flex-end' }
              }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => handleAssignVMClick(fixture)}
                  startIcon={<LocationOnIcon />}
                  size={isMobile ? 'small' : 'medium'}
                  fullWidth={isMobile}
                  sx={{
                    bgcolor: 'primary.main',
                    '&:hover': {
                      bgcolor: 'primary.dark',
                      transform: 'translateY(-1px)',
                      transition: 'transform 0.2s ease-in-out'
                    }
                  }}
                >
                  Assign VM
                </Button>
              </Box>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {selectedFixture && (
        <Dialog
          open={!!selectedFixture}
          onClose={() => setSelectedFixture(null)}
          maxWidth="xl"
          fullWidth
          PaperProps={{
            sx: {
              maxWidth: { xs: '95vw', sm: '90vw' },
              minWidth: { xs: '95vw', sm: '80vw', md: '70vw' },
              maxHeight: { xs: '95vh', sm: '90vh' },
              margin: { xs: '8px', sm: '16px' }
            }
          }}
        >
          <DialogTitle sx={{ pb: 1, pt: { xs: 1.5, sm: 2 } }}>
            <Typography 
              variant="h5" 
              sx={{ 
                color: '#0A2647', 
                fontWeight: 600,
                fontSize: { xs: '1.2rem', sm: '1.5rem' },
                pr: { xs: 4, sm: 5 } // Add padding for the close button
              }}
            >
              Assign VM to {selectedFixture.name}
            </Typography>
            <IconButton
              aria-label="close"
              onClick={() => setSelectedFixture(null)}
              sx={{
                position: 'absolute',
                right: { xs: 4, sm: 8 },
                top: { xs: 4, sm: 8 },
                color: (theme) => theme.palette.grey[500]
              }}
            >
              <CancelIcon />
            </IconButton>
          </DialogTitle>

          <DialogContent dividers sx={{ p: { xs: 1.5, sm: 2 } }}>
            {step === 1 ? (
              <Box component="form" noValidate autoComplete="off">
                <TextField
                  fullWidth
                  label="Fixture Name (from config)"
                  name="name"
                  value={formData.name}
                  onChange={handleFormChange}
                  margin="normal"
                  size={isMobile ? 'small' : 'medium'}
                  sx={{ mb: { xs: 1.5, sm: 2 } }}
                  disabled
                />
                <Box sx={{ mb: { xs: 1.5, sm: 2 } }}>
                  <input
                    accept="image/*"
                    type="file"
                    id="upload-reference-image"
                    style={{ display: 'none' }}
                    onChange={handleAssignVMFileChange}
                  />
                  <label htmlFor="upload-reference-image">
                    <Button 
                      variant="outlined" 
                      component="span" 
                      fullWidth={isMobile} 
                      size={isMobile ? 'small' : 'medium'} 
                      sx={{ mb: 1 }}
                    >
                      {isMobile ? 'Upload Image' : 'Upload Assign VM Reference Image'}
                    </Button>
                    {formData.referenceImage && (
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          mt: 1,
                          fontSize: { xs: '0.75rem', sm: '0.875rem' },
                          wordBreak: 'break-word'
                        }}
                      >
                        {formData.referenceImage.name}
                      </Typography>
                    )}
                  </label>
                </Box>
                <TextField
                  fullWidth
                  label="Assign VM Description"
                  name="description"
                  value={formData.description}
                  onChange={handleFormChange}
                  margin="normal"
                  multiline
                  rows={isMobile ? 3 : 4}
                  size={isMobile ? 'small' : 'medium'}
                />
              </Box>
            ) : (
              renderLocationSelection()
            )}
          </DialogContent>
          <DialogActions sx={{ p: { xs: 1.5, sm: 2 }, flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'stretch', sm: 'center' } }}>
            <Button 
              onClick={() => setSelectedFixture(null)}
              fullWidth={isMobile}
              size={isMobile ? 'small' : 'medium'}
              sx={{ mb: { xs: 1, sm: 0 } }}
            >
              Cancel
            </Button>
            {step === 1 ? (
              <Button
                variant="contained"
                color="primary"
                onClick={handleSaveAndNext}
                startIcon={<LocationOnIcon />}
                size={isMobile ? 'small' : 'medium'}
                fullWidth={isMobile}
                sx={{ ml: { xs: 0, sm: 1 } }}
              >
                {isMobile ? 'Next' : 'Next: Location Selection'}
              </Button>
            ) : (
              <Button
                variant="contained"
                color="primary"
                onClick={handleSubmit}
                startIcon={<SaveIcon />}
                size={isMobile ? 'small' : 'medium'}
                fullWidth={isMobile}
                sx={{ ml: { xs: 0, sm: 1 } }}
              >
                Save Configuration
              </Button>
            )}
          </DialogActions>
        </Dialog>
      )}

      <Dialog 
        open={isCreateModalOpen} 
        onClose={handleCreateModalClose} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: {
            width: { xs: '95%', sm: '80%' },
            maxWidth: { xs: '95vw', sm: '500px' },
            margin: { xs: '8px', sm: '16px' }
          }
        }}
      >
        <DialogTitle sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>Create New Fixture</DialogTitle>
        <DialogContent dividers sx={{ p: { xs: 1.5, sm: 2 } }}>
          <Box component="form" noValidate autoComplete="off" sx={{ mt: 1 }}>
            <TextField
              fullWidth
              label="Fixture Name"
              name="name"
              value={newFixtureData.name}
              onChange={handleNewFixtureChange}
              margin="normal"
              size="small"
              required
            />
            <Box sx={{ my: { xs: 1.5, sm: 2 }, textAlign: 'center' }}>
              <input
                accept="image/*"
                type="file"
                id="upload-new-fixture-image"
                style={{ display: 'none' }}
                onChange={handleNewFixtureFileChange}
              />
              <label htmlFor="upload-new-fixture-image">
                <Button 
                  variant="outlined" 
                  component="span" 
                  size="small"
                  fullWidth={isMobile}
                >
                  Upload Fixture Image
                </Button>
              </label>
              {/* {newFixtureData.imagePreview && (
                <Box sx={{ mt: { xs: 1.5, sm: 2 } }}>
                  <Typography variant="caption">Image Preview:</Typography>
                  <Box
                    component="img"
                    src={newFixtureData.imagePreview}
                    alt="Fixture Preview"
                    sx={{
                      mt: 1,
                      width: { xs: 80, sm: 100 },
                      height: { xs: 80, sm: 100 },
                      objectFit: 'cover',
                      borderRadius: 1,
                      border: '1px solid lightgrey'
                    }}
                  />
                </Box>
              )} */}
            </Box>
            <TextField
              fullWidth
              label="Brands (comma-separated)"
              name="Brands"
              value={newFixtureData.Brands}
              onChange={handleNewFixtureChange}
              margin="normal"
              size="small"
              helperText="e.g., Adidas, Puma, Nike"
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: { xs: 1.5, sm: 2 }, flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'stretch', sm: 'center' } }}>
          <Button 
            onClick={handleCreateModalClose}
            fullWidth={isMobile}
            size={isMobile ? 'small' : 'medium'}
            sx={{ mb: { xs: 1, sm: 0 } }}
          >
            Cancel
          </Button>
          <Button 
            variant="contained" 
            onClick={handleCreateFixtureSubmit} 
            disabled={!newFixtureData.name} 
            fullWidth={isMobile}
            size={isMobile ? 'small' : 'medium'}
            sx={{ ml: { xs: 0, sm: 1 } }}
          >
            Create Fixture
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  </Container>
  );
};

export default VMFixtures;
